import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { finalize } from 'rxjs';

import { Session } from '../../../../services/session';
import { AcademicYearService } from '../../../../services/academic-year';
import { ApiEndpoints } from '../../../../core/api-endpoints';
import { LoadingIndicator } from '../../../../shared/loading-indicator/loading-indicator';
import { FlashService } from '../../../../services/flash';
import { OnboardingService } from '../../../../services/school-onboarding';

@Component({
  selector: 'app-step-two',
  imports: [FormsModule, LoadingIndicator, RouterLink],
  templateUrl: './step-two.html',
  styleUrl: './step-two.css',
})
export class StepTwo implements OnInit {
  private readonly flashService = inject(FlashService);

  private readonly onboardingService = inject(OnboardingService);

  protected academicYearName = '';
  protected startDate = '';
  protected endDate = '';
  protected currentAcademicYear = true;

  protected isLoading = signal(false);
  protected schoolId?: string;

  constructor(
    private readonly session: Session,
    private readonly router: Router,
    private readonly academicYearService: AcademicYearService,
  ) {
    const token = sessionStorage.getItem('token');

    if (!token) {
      void this.router.navigate(['/']);
      return;
    }

    this.schoolId = this.session.decodeToken(token)?.school_id;

    if (!this.schoolId) {
      void this.router.navigate(['/']);
    }
  }

  ngOnInit(): void {
    const existingAcademicYear = this.onboardingService.getAcademicYear();

    if (!existingAcademicYear) {
      return;
    }

    this.academicYearName = existingAcademicYear.name;

    this.startDate = this.formatDateForInput(existingAcademicYear.start_date);

    this.endDate = this.formatDateForInput(existingAcademicYear.end_date);
  }

  protected createAcademicYear(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields.');

      form.control.markAllAsTouched();
      return;
    }

    if (!this.schoolId) {
      void this.router.navigate(['/']);
      return;
    }

    if (this.isLoading()) {
      return;
    }

    const existingAcademicYear = this.onboardingService.getAcademicYear();

    if (existingAcademicYear) {
      void this.router.navigate(['/school-onboarding/academic-term-configuration']);

      return;
    }

    this.isLoading.set(true);

    const academicYearForm = {
      school_id: this.schoolId,
      name: this.academicYearName,
      start_date: this.startDate,
      end_date: this.endDate,
      current_academic_year: this.currentAcademicYear,
    };

    this.academicYearService
      .createAcademicYear(`${ApiEndpoints.url}/academic-year`, academicYearForm)
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
        }),
      )
      .subscribe({
        next: (result) => {
          this.onboardingService.setAcademicYear({
            id: result.payload.id,
            name: result.payload.name,
            start_date: result.payload.start_date,
            end_date: result.payload.end_date,
          });

          this.flashService.success('Successfully added academic year.');

          setTimeout(() => {
            void this.router.navigate(['/school-onboarding/academic-term-configuration']);
          }, 1500);
        },
        error: (err) => {
          this.flashService.failure(err?.error?.message ?? 'Could not create academic year. Try again.');
        },
      });
  }

  private formatDateForInput(value: string): string {
    return value ? value.substring(0, 10) : '';
  }
}
